import { Typography } from 'app/components';
import { useEffect, useRef } from 'react';
import { i18n } from 'app/utils/i18n';
import { useDocumentTaxonomyConfig, useCreateDocumentTaxonomyConfig, useUpdateDocumentTaxonomyConfig } from 'app/api/tag-queries/document-taxonomy-config';
import { ActiveTaxonomiesList, DateDefaultsSection, UnitDefaultsSection, PropertyDefaultsSection } from './components';
import { GeneralInformationSection } from './GeneralInformationSection';
import { useBatchedConfigUpdate } from './hooks';

import './Taxonomies.scss';

export const Taxonomies = ({ documentId }: { documentId: string }) => {
  const documentSuperId = documentId ? parseInt(documentId, 10) : undefined;
  const configCreatedRef = useRef(false);

  const { data: config, isLoading: isConfigLoading } = useDocumentTaxonomyConfig(documentSuperId);
  const { mutate: createConfig } = useCreateDocumentTaxonomyConfig();
  const { mutate: updateConfig } = useUpdateDocumentTaxonomyConfig();
  const { update: batchedUpdate } = useBatchedConfigUpdate({ configId: config?.id, updateConfig });

  useEffect(() => {
    if (!isConfigLoading && config === null && documentSuperId && !configCreatedRef.current) {
      configCreatedRef.current = true;
      createConfig({ documentSuperId });
    }
  }, [config, isConfigLoading, documentSuperId, createConfig]);

  if (!config) {
    return (
      <div className="tagging-settings">
        <div className="text-center py-4">
          <i className="fa-regular fa-spinner fa-spin fa-2x mb-2" />
          <Typography variant="body-7">{i18n('tag.taxonomy.settings.loading')}</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="tagging-settings">
      <ActiveTaxonomiesList configId={config.id} documentSuperId={config.documentSuperId} taxonomySelections={config.taxonomySelections} />

      <Typography variant="body-8" fontWeight="semibold">
        {i18n('tag.taxonomy.settings')}
      </Typography>

      <section className="tagging-settings__taxonomy-form">
        {/* General information — LEI + publisher metadata required for the package export. */}
        <GeneralInformationSection
          entity={config.entity}
          publisherUrl={config.publisherUrl}
          publisherCountry={config.publisherCountry}
          packageName={config.packageName}
          batchedUpdate={batchedUpdate}
        />

        {config.reportingEntryPoints && config.reportingEntryPoints.length > 0 && (
          <div className="tagging-settings__reporting-form">
            <Typography variant="body-7" fontWeight="semibold">
              {i18n('tag.taxonomy.reportingForm') || 'Reporting form'}
            </Typography>
            <select
              className="form-select form-select-sm"
              value={config.familySettings?.reportingEntryPoint ?? ''}
              onChange={(e) => batchedUpdate({ familySettings: { ...config.familySettings, reportingEntryPoint: e.target.value || null } })}
            >
              <option value="">{i18n('tag.taxonomy.reportingForm.none') || '— Select reporting form —'}</option>
              {config.reportingEntryPoints.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Default Values Sections */}
        <UnitDefaultsSection configId={config.id} documentSuperId={config.documentSuperId} currencySelections={config.currencySelections} />

        <DateDefaultsSection configId={config.id} documentSuperId={config.documentSuperId} contextSelections={config.contextSelections} />

        <PropertyDefaultsSection configId={config.id} config={config} />
      </section>
    </div>
  );
};
